export const SCHEDULED_PAYMENT_METHOD_CODES = [
  'merchant-payment',
  'moru-credit',
  'cash-on-delivery',
] as const;

export function isScheduledPaymentMethod(code: string): boolean {
  return (SCHEDULED_PAYMENT_METHOD_CODES as readonly string[]).includes(code);
}

export type ScheduledPaymentChoice = 'MORU_CREDIT' | 'COD';

export interface ScheduledLineRecurrence {
  itemType?: string | null;
  frequency?: string | null;
  intervalStep?: number | null;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  daysOfWeek?: readonly string[] | null;
}

const SCHEDULE_DAY_INDEX: Readonly<Record<string, number>> = {
  SUN: 0,
  SUNDAY: 0,
  MON: 1,
  MONDAY: 1,
  TUE: 2,
  TUESDAY: 2,
  WED: 3,
  WEDNESDAY: 3,
  THU: 4,
  THURSDAY: 4,
  FRI: 5,
  FRIDAY: 5,
  SAT: 6,
  SATURDAY: 6,
};

export function toScheduledPaymentChoice(
  code: string,
): ScheduledPaymentChoice | null {
  if (code === 'cash-on-delivery') return 'COD';
  if (code === 'merchant-payment' || code === 'moru-credit') {
    return 'MORU_CREDIT';
  }
  return null;
}

function kathmanduDateParts(date: Date): {
  year: number;
  month: number;
  day: number;
} {
  const values = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kathmandu',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .formatToParts(date)
    .reduce<Record<string, number>>((result, part) => {
      if (part.type !== 'literal') {
        result[part.type] = Number(part.value);
      }
      return result;
    }, {});

  return {
    year: values.year,
    month: values.month,
    day: values.day,
  };
}

function formatDate(year: number, month: number, day: number): string {
  return `${year.toString().padStart(4, '0')}-${month
    .toString()
    .padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
}

function parseDateKey(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

function toKathmanduDateKey(
  value: string | Date | null | undefined,
): string | null {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return parseDateKey(value) ? value : null;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = kathmanduDateParts(date);
  return formatDate(parts.year, parts.month, parts.day);
}

/**
 * Mirrors the backend's inclusive Kathmandu recurrence expansion. The legacy
 * totalDeliveries field is deliberately not accepted as an input because it is
 * only a client assertion, not the source of truth for a schedule.
 */
export function deriveScheduledOccurrenceCount(
  schedule: ScheduledLineRecurrence,
): number | null {
  const startDateKey = toKathmanduDateKey(schedule.startDate);
  if (!startDateKey) return null;
  if (schedule.itemType === 'future') return 1;
  if (schedule.itemType !== 'recurring') return null;

  const endDateKey = toKathmanduDateKey(schedule.endDate ?? schedule.startDate);
  if (!endDateKey) return null;
  const start = parseDateKey(startDateKey);
  const end = parseDateKey(endDateKey);
  if (!start || !end || end < start) return null;

  const horizon = Math.round((end.getTime() - start.getTime()) / 86_400_000);
  if (schedule.frequency === 'daily') return horizon + 1;

  if (schedule.frequency === 'custom') {
    const interval = Number(schedule.intervalStep);
    if (!Number.isInteger(interval) || interval <= 0) return null;
    return Math.floor(horizon / interval) + 1;
  }

  if (schedule.frequency === 'weekly') {
    const selectedDayIndices = new Set<number>();
    const configuredDays = schedule.daysOfWeek ?? [];
    if (configuredDays.length === 0) {
      selectedDayIndices.add(start.getUTCDay());
    } else {
      for (const configuredDay of configuredDays) {
        const dayIndex =
          SCHEDULE_DAY_INDEX[String(configuredDay).trim().toUpperCase()];
        if (dayIndex === undefined) return null;
        selectedDayIndices.add(dayIndex);
      }
    }

    let count = 0;
    for (let offset = 0; offset <= horizon; offset += 1) {
      const date = new Date(start);
      date.setUTCDate(date.getUTCDate() + offset);
      if (selectedDayIndices.has(date.getUTCDay())) count += 1;
    }
    return count > 0 ? count : null;
  }

  return null;
}

export function getMinimumScheduledDate(now = new Date()): string {
  const current = kathmanduDateParts(now);
  const tomorrow = new Date(
    Date.UTC(current.year, current.month - 1, current.day + 1),
  );
  return formatDate(
    tomorrow.getUTCFullYear(),
    tomorrow.getUTCMonth() + 1,
    tomorrow.getUTCDate(),
  );
}

export function isScheduledDateOnOrAfterMinimum(
  value: string | Date | null | undefined,
  now = new Date(),
): boolean {
  const dateKey = toKathmanduDateKey(value);
  return dateKey !== null && dateKey >= getMinimumScheduledDate(now);
}
